import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getMemberStats, getMembers, getMemberDemographics, getMembershipReport } from '../../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import PhoneLink from '../../components/PhoneLink';

const COLORS = ['#4f46e5', '#16a34a', '#d97706', '#dc2626', '#0891b2'];

const currentYear = new Date().getFullYear();

const MembershipStatus = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState({ total: 0, grandTotal: 0, members: 0, inactive: 0, men: 0, women: 0, boys: 0, girls: 0, unknownAge: 0, husbands: 0, wives: 0, sons: 0, daughters: 0, deceasedDeps: 0, transferredDeps: 0 });
  const [members, setMembers] = useState([]);
  const [demoList, setDemoList] = useState([]);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState('member');
  const [demoTab, setDemoTab] = useState(null); // null = show status view, 'men'/'women'/'boys'/'girls' = show demo list

  useEffect(() => {
    getMemberStats().then(r => setStats(r.data || { total: 0, grandTotal: 0, members: 0, inactive: 0, men: 0, women: 0, boys: 0, girls: 0, unknownAge: 0, husbands: 0, wives: 0, sons: 0, daughters: 0, deceasedDeps: 0, transferredDeps: 0 })).catch(() => {});
    getMembershipReport().then(r => setReport(r.data || null)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!demoTab) {
      getMembers({ status: activeTab }).then(r => setMembers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }
  }, [activeTab, demoTab]);

  useEffect(() => {
    if (demoTab) {
      getMemberDemographics(demoTab).then(r => setDemoList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }
  }, [demoTab]);

  const statusColors = { member: '#16a34a', inactive: '#dc2626', transferred: '#2563eb', suspended: '#f59e0b', changed_religion: '#6b7280', deceased: '#374151', other: '#9ca3af' };
  const statusBg     = { member: '#dcfce7', inactive: '#fee2e2', transferred: '#dbeafe', suspended: '#fef3c7', changed_religion: '#f3f4f6', deceased: '#e5e7eb', other: '#f3f4f6' };
  const statusLabels = { member: t('membershipStatus.active'), inactive: t('status.inactive'), transferred: t('status.transferred'), suspended: t('status.suspended'), changed_religion: t('status.changed_religion'), deceased: t('status.deceased'), other: 'Other' };
  // Fixed display order so zero-count statuses still appear (greyed out).
  const STATUS_ORDER = ['member', 'inactive', 'transferred', 'changed_religion', 'deceased', 'suspended', 'other'];
  const byStatusMap = (report?.byStatus || []).reduce((m, s) => { m[s.membership_status] = s.count; return m; }, {});
  const pieData = report
    ? report.byStatus.map(s => ({ name: statusLabels[s.membership_status] || s.membership_status, value: s.count, color: statusColors[s.membership_status] || '#9ca3af' })).filter(d => d.value > 0)
    : [{ name: t('membershipStatus.active'), value: stats.members, color: '#16a34a' }];

  const statusTabs = report
    ? report.byStatus.map(s => ({ key: s.membership_status, label: `${statusLabels[s.membership_status] || s.membership_status} (${s.count})` }))
    : [{ key: 'member', label: `${t('membershipStatus.active')} (${stats.members})` }];

  // Whole-population by gender (members + dependents). Husband/wife/son/daughter
  // breakdowns were removed — the user wanted a single Male/Female view.
  const demoTabs = [
    { key: 'male',   label: 'Male',   value: stats.male   || 0, color: '#2563eb', bg: '#dbeafe' },
    { key: 'female', label: 'Female', value: stats.female || 0, color: '#db2777', bg: '#fce7f3' },
  ];

  const lifeTabs = [
    { key: 'deceased', label: t('status.deceased'), value: stats.deceasedDeps || 0, color: '#1f2937', bg: '#e5e7eb' },
    { key: 'transferred', label: t('status.transferred'), value: stats.transferredDeps || 0, color: '#1d4ed8', bg: '#dbeafe' },
  ];

  const typeColor = { Member: 'pill-green', Wife: 'pill-purple', Husband: 'pill-blue', Son: 'pill-blue', Daughter: 'pill-purple', Deceased: 'pill-gray', Transferred: 'pill-blue' };
  const typeLabels = {
    Member: t('membershipStatus.memberType'),
    Wife: t('membershipStatus.wifeType'),
    Husband: t('membershipStatus.husbandType'),
    Son: t('membershipStatus.sonType'),
    Daughter: t('membershipStatus.daughterType'),
    Deceased: t('membershipStatus.deceasedType'),
    Transferred: t('membershipStatus.transferredType'),
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.membershipStatus')}</h1><p>{t('common.total')}: <strong>{stats.grandTotal}</strong></p></div>
      </div>

      {/* Demographics Cards */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{t('dashboard.demographics')}</span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            {stats.families || 0} {(stats.families || 0) === 1 ? 'family' : 'families'} · {stats.total || 0} people
          </span>
        </div>
        <div className="card-body">
          {/* Overview row — Families + Male + Female. Members/Inactive headline numbers
              moved to the By Status row below to avoid the "10 Members" confusion. */}
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Overview</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
            <div style={{ textAlign: 'center', background: '#eef2ff', borderRadius: 10, padding: '14px 8px' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: '#4f46e5' }}>{stats.families || 0}</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Families</div>
            </div>
            {demoTabs.map(d => (
              <div key={d.key}
                onClick={() => setDemoTab(demoTab === d.key ? null : d.key)}
                style={{ textAlign: 'center', background: demoTab === d.key ? d.color : d.bg, borderRadius: 10, padding: '14px 8px', cursor: 'pointer', transition: 'all 0.15s', border: demoTab === d.key ? `2px solid ${d.color}` : '2px solid transparent' }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: demoTab === d.key ? '#fff' : d.color }}>{d.value}</div>
                <div style={{ fontSize: 12, color: demoTab === d.key ? '#fff' : '#6b7280', marginTop: 2 }}>{d.label}</div>
              </div>
            ))}
          </div>

          {/* By Status — one card per membership_status. Clicking sets the
              distribution table filter to that status. Zero-counts greyed out. */}
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>By Status</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 16 }}>
            {STATUS_ORDER.map(key => {
              const count = byStatusMap[key] || 0;
              const active = activeTab === key;
              const empty = count === 0;
              const color = statusColors[key] || '#9ca3af';
              const bg = statusBg[key] || '#f3f4f6';
              return (
                <div key={key}
                  onClick={() => { setActiveTab(key); setDemoTab(null); }}
                  style={{
                    textAlign: 'center',
                    background: active ? color : (empty ? '#fafafa' : bg),
                    borderRadius: 10,
                    padding: '12px 6px',
                    cursor: 'pointer',
                    border: active ? `2px solid ${color}` : '2px solid transparent',
                    opacity: empty ? 0.55 : 1,
                  }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: active ? '#fff' : color }}>{count}</div>
                  <div style={{ fontSize: 11, color: active ? '#fff' : '#6b7280', marginTop: 2, textTransform: 'capitalize' }}>{statusLabels[key]}</div>
                </div>
              );
            })}
          </div>

          {(stats.deceasedDeps > 0 || stats.transferredDeps > 0) && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{t('membershipStatus.inactiveDependents')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
                {lifeTabs.map(d => (
                  <div key={d.key}
                    onClick={() => setDemoTab(demoTab === d.key ? null : d.key)}
                    style={{ textAlign: 'center', background: demoTab === d.key ? d.color : d.bg, borderRadius: 10, padding: '14px 8px', cursor: 'pointer', transition: 'all 0.15s', border: demoTab === d.key ? `2px solid ${d.color}` : '2px solid transparent' }}>
                    <div style={{ fontSize: 26, fontWeight: 800, color: demoTab === d.key ? '#fff' : d.color }}>{d.value}</div>
                    <div style={{ fontSize: 12, color: demoTab === d.key ? '#fff' : '#6b7280', marginTop: 2 }}>{d.label}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Demographics List */}
      {demoTab && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{[...demoTabs, ...lifeTabs].find(d => d.key === demoTab)?.label} — {demoList.length} {t('membershipStatus.people')}</span>
            <button className="btn btn-sm btn-secondary" onClick={() => setDemoTab(null)}>{t('common.close')}</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr><th>#</th><th>{t('common.name')}</th><th>{t('membershipStatus.type')}</th><th>{t('members.age')}</th><th>{t('members.spiritualFather')}</th></tr>
              </thead>
              <tbody>
                {demoList.length === 0 ? (
                  <tr><td colSpan={5}><div className="empty-state"><p>{t('membershipStatus.noRecordsFound')}</p></div></td></tr>
                ) : demoList.map((p, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td><strong>{p.name}</strong></td>
                    <td><span className={`pill ${typeColor[p.type] || 'pill-gray'}`}>{typeLabels[p.type] || p.type}</span></td>
                    <td>{p.age ? `${p.age} ${t('allMembers.yrs')}` : '—'}</td>
                    <td>{p.spiritual_father || <span style={{ color: '#9ca3af' }}>{t('members.unassigned')}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Status Distribution */}
      {!demoTab && (
        <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 2fr', gap: 20, marginBottom: 24 }}>
          <div className="card">
            <div className="card-header">{t('membershipStatus.distribution')}</div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 8 }}>
                {pieData.map((d, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                    <span style={{ color: '#6b7280' }}>{d.name}</span>
                    <strong style={{ color: d.color }}>{d.value}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header" style={{ display: 'flex', gap: 8, padding: '12px 20px' }}>
              {statusTabs.map(tab => (
                <button key={tab.key} className={`btn btn-sm ${activeTab === tab.key ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab(tab.key)}>{tab.label}</button>
              ))}
            </div>
            <div style={{ overflow: 'auto', maxHeight: 260 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>{t('common.name')}</th>
                    <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>{t('common.phone')}</th>
                    <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>{t('members.spiritualFather')}</th>
                    <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>{t('membershipStatus.joinDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m, i) => (
                    <tr key={m.id} style={{ borderBottom: i < members.length-1 ? '1px solid #f3f4f6' : 'none' }}>
                      <td style={{ padding: '10px 16px', fontSize: 13 }}>{m.first_name} {m.last_name}</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: '#6b7280' }}><PhoneLink phone={m.phone} whatsapp={m.whatsapp} /></td>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: '#6b7280' }}>{m.spiritual_father_name || '—'}</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: '#6b7280' }}>{m.join_date || '—'}</td>
                    </tr>
                  ))}
                  {members.length === 0 && (
                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: '#9ca3af', fontSize: 13 }}>{t('membershipStatus.noRecords')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {/* Growth & Department */}
      {report && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, marginBottom: 20 }}>
            <div className="card">
              <div className="card-header">{t('membershipStatus.monthlyJoins')}</div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={[...report.monthly].reverse()} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#4f46e5" radius={[4,4,0,0]} name={t('membershipStatus.newMembers')} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="card">
              <div className="card-header">{t('membershipStatus.byGender')}</div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={report.byGender.map(g => ({ name: g.gender || t('followup.unknown'), value: g.count }))} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      {report.byGender.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {report.byDept?.length > 0 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header">{t('membershipStatus.byDepartment')}</div>
              <div className="card-body" style={{ padding: 0 }}>
                <table>
                  <thead><tr><th>#</th><th>{t('membershipStatus.department')}</th><th>{t('membershipStatus.membersCol')}</th><th>{t('membershipStatus.percentOfTotal')}</th></tr></thead>
                  <tbody>
                    {report.byDept.map((d, i) => (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td>{d.department}</td>
                        <td>{d.count}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ height: 8, width: `${Math.round(d.count / report.total * 100)}%`, maxWidth: 120, background: '#4f46e5', borderRadius: 4, minWidth: 4 }} />
                            {report.total > 0 ? Math.round(d.count / report.total * 100) : 0}%
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MembershipStatus;
